import { HttpErrorResponse } from '@angular/common/http';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { ActionWithPayload } from '../actions/auth.actions';
import * as fromLicenceActions from '../actions/licence.actions';
import * as fromActions from '../actions/organization.actions';
import * as fromUserManagementActions from '../actions/users.management.actions';
import { GetOrganizationDto } from '../dtos/getOrganizationDto.interface';
import { GetUserDto } from '../dtos/getUserDto.type';
import { InvitationsDto } from '../dtos/invitationsDto.interface';
import { LicenceStatusDto } from '../dtos/licenceStatusDto';
import { OrganizationSearchResultDto, UserStateDto } from '../dtos/organizationSearchDto.interface';
import { CPMMState } from '../enums/cpmm-state.enum';
import { OrganizationSettings } from '../interfaces/organization-settings.interface';

export const organizationFeatureKey = 'aumicoUsersOrganizationMicroservice';

export const selectOrganizationFeature = createFeatureSelector<FeatureState>(organizationFeatureKey);

// State Declarations - START

export interface FeatureState extends EntityState<GetUserDto> {
    currentOrganization: GetOrganizationDto | undefined;
    error: HttpErrorResponse | undefined;
    organizations: GetOrganizationDto[] | undefined;
    searchResults: OrganizationSearchResultDto[] | undefined;
    currentOrganizationSettings: OrganizationSettings | undefined;
    licenceStatus?: LicenceStatusDto | undefined;
}
export interface AppState {
    aumicoUsersOrganizationMicroservice: FeatureState;
}

// State Declarations - END

// Selectors Declarations - START

export function selectUserId(a: GetUserDto): string {
    return a.id;
}

export const adapter: EntityAdapter<GetUserDto> = createEntityAdapter<GetUserDto>({
    selectId: selectUserId,
});

export const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

export const selectFeature = (state: AppState) => state.aumicoUsersOrganizationMicroservice;

export const selectEntity = createSelector(selectEntities, (entities, props) => entities[props.id]);

export const selectUserEntities = createSelector(selectOrganizationFeature, selectEntities);

export const selectAllUsers = createSelector(selectOrganizationFeature, selectAll);

export const selectAllPossibleAssignees = createSelector(selectAllUsers, (users: GetUserDto[]) =>
    users.filter((user: GetUserDto) => !user.isInvitee && !user.isDeleted),
);

export const selectError = createSelector(selectOrganizationFeature, (state: FeatureState) => state.error);

export const selectCurrentOrganization = createSelector(
    selectOrganizationFeature,
    (state: FeatureState) => state.currentOrganization,
);

export const selectLicenceStatus = createSelector(
    selectOrganizationFeature,
    (state: FeatureState) => state.licenceStatus,
);

export const selectCurrentOrganizationSettings = createSelector(
    selectOrganizationFeature,
    (state: FeatureState) => state.currentOrganizationSettings,
);

export const selectAllOrganizations = createSelector(
    selectOrganizationFeature,
    (state: FeatureState) => state.organizations,
);

export const selectSearchResults = createSelector(
    selectOrganizationFeature,
    (state: FeatureState) => state.searchResults,
);

export const selectCurrentOrganizationName = createSelector(
    selectCurrentOrganization,
    (currentOrganization: GetOrganizationDto | undefined) => currentOrganization?.name || '',
);

export const selectCurrentOrganizationAbv = createSelector(selectCurrentOrganizationName, (name: string) =>
    name.substring(0, 2),
);

/**
 * Returns true if the current organization has CPMM ACTIVE
 */
export const selectIsCpmmActive = createSelector(
    selectCurrentOrganization,
    (org: GetOrganizationDto | undefined): boolean => org?.cpmmActivationDetails?.activationState === CPMMState.ACTIVE,
);

// Selectors Declarations - END

// Reducer Declarations - START

export const initialState: FeatureState = adapter.getInitialState({
    currentOrganization: undefined,
    error: undefined,
    organizations: undefined,
    currentOrganizationSettings: undefined,
    licenceStatus: undefined,
    searchResults: undefined,
});

const organizationReducer = createReducer(
    initialState,

    // Organization
    on(fromActions.getCurrentOrganization.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.getCurrentOrganization.success, (state: FeatureState, action) => ({
        ...state,
        currentOrganization: action.payload,
        error: undefined,
    })),
    on(fromActions.getCurrentOrganization.failure, (state: FeatureState, action) => ({
        ...state,
        currentOrganization: undefined,
        error: action.payload,
    })),

    on(fromActions.updateCurrentOrganization.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.updateCurrentOrganization.success, (state: FeatureState, action) => ({
        ...state,
        currentOrganization: action.payload,
        error: undefined,
    })),
    on(fromActions.updateCurrentOrganization.failure, (state: FeatureState, action) => ({
        ...state,
        currentOrganization: undefined,
        error: action.payload,
    })),

    on(fromActions.setCurrentOrganization, (state: FeatureState, action) => ({
        ...state,
        currentOrganization: { ...state.currentOrganization, ...action.payload },
    })),

    on(fromActions.resetCurrentOrganization, (state: FeatureState, action) => ({
        ...state,
        currentOrganization: undefined,
    })),

    on(fromActions.getAllOrganizations.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.getAllOrganizations.success, (state: FeatureState, action) => ({
        ...state,
        organizations: action.payload,
        error: undefined,
    })),
    on(fromActions.getAllOrganizations.failure, (state: FeatureState, action) => ({
        ...state,
        organizations: undefined,
        error: action.payload,
    })),
    on(fromActions.searchOrganizations.search, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.searchOrganizations.success, (state: FeatureState, action) => ({
        ...state,
        searchResults: action.payload,
        error: undefined,
    })),
    on(fromActions.searchOrganizations.failure, (state: FeatureState, action) => ({
        ...state,
        searchResults: undefined,
        error: action.payload,
    })),
    on(fromUserManagementActions.userManagement.changeUserRoleSuccess, (state: FeatureState, action) => {
        const updatedSearchResults = state.searchResults.map((org) => {
            const userIndex = org.users.findIndex((user) => user.internalId === action.payload.id);
            if (userIndex > -1) {
                const updatedUsers = [...org.users];
                const updatedUser = {
                    ...updatedUsers[userIndex],
                    organizationRole: action.payload.organizationRole,
                };
                updatedUsers[userIndex] = updatedUser;
                return { ...org, users: updatedUsers };
            }
            return org;
        });

        return {
            ...state,
            searchResults: updatedSearchResults,
            error: undefined,
        };
    }),
    on(fromUserManagementActions.userManagement.promoteUserToAdminSuccess, (state: FeatureState, action) => {
        const updatedSearchResults = state.searchResults.map((org) => {
            const userIndex = org.users.findIndex((user) => user.internalId === action.payload);
            if (userIndex > -1) {
                const updatedUsers = [...org.users];
                const updatedUser = {
                    ...updatedUsers[userIndex],
                    appRole: 'APP_ADMIN',
                };
                updatedUsers[userIndex] = updatedUser;
                return { ...org, users: updatedUsers };
            }
            return org;
        });

        return {
            ...state,
            searchResults: updatedSearchResults,
            error: undefined,
        };
    }),
    on(fromUserManagementActions.userManagement.demoteUserFromAdminSuccess, (state: FeatureState, action) => {
        const updatedSearchResults = state.searchResults.map((org) => {
            const userIndex = org.users.findIndex((user) => user.internalId === action.payload);
            if (userIndex > -1) {
                const updatedUsers = [...org.users];
                const updatedUser = {
                    ...updatedUsers[userIndex],
                    appRole: 'APP_USER',
                };
                updatedUsers[userIndex] = updatedUser;
                return { ...org, users: updatedUsers };
            }
            return org;
        });

        return {
            ...state,
            searchResults: updatedSearchResults,
            error: undefined,
        };
    }),
    on(fromUserManagementActions.userManagement.blockUserSuccess, (state: FeatureState, action) => {
        const updatedSearchResults = state.searchResults.map((org) => {
            const userIndex = org.users.findIndex((user) => user.internalId === action.payload);
            if (userIndex > -1) {
                const updatedUsers = [...org.users];
                const updatedUser = {
                    ...updatedUsers[userIndex],
                    userState: UserStateDto.SUSPENDED,
                };
                updatedUsers[userIndex] = updatedUser;
                return { ...org, users: updatedUsers };
            }
            return org;
        });

        return {
            ...state,
            searchResults: updatedSearchResults,
            error: undefined,
        };
    }),
    on(fromUserManagementActions.userManagement.unblockUserSuccess, (state: FeatureState, action) => {
        const updatedSearchResults = state.searchResults.map((org) => {
            const userIndex = org.users.findIndex((user) => user.internalId === action.payload);
            if (userIndex > -1) {
                const updatedUsers = [...org.users];
                const updatedUser = {
                    ...updatedUsers[userIndex],
                    userState: UserStateDto.ACTIVE,
                };
                updatedUsers[userIndex] = updatedUser;
                return { ...org, users: updatedUsers };
            }
            return org;
        });

        return {
            ...state,
            searchResults: updatedSearchResults,
            error: undefined,
        };
    }),
    on(fromActions.updateOrganizationState.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.updateOrganizationState.success, (state: FeatureState, action) => {
        let updatedState = { ...state, error: undefined };
        if (state.searchResults !== undefined) {
            const updatedSearchResults = state.searchResults.map((org) => {
                if (org.organizationId === action.payload.id) {
                    return { ...org, state: action.payload.state };
                }
                return org;
            });

            updatedState = { ...updatedState, searchResults: updatedSearchResults };
        }
        if (updatedState.organizations === undefined) {
            return updatedState;
        }
        const organizationIndex = updatedState.organizations.findIndex(
            (organization) => organization.id === action.payload.id,
        );
        const updatedOrganization = { ...updatedState.organizations[organizationIndex], state: action.payload.state };
        const updatedOrganizationsList = [...updatedState.organizations];
        updatedOrganizationsList[organizationIndex] = updatedOrganization;
        return { ...updatedState, organizations: updatedOrganizationsList };
    }),
    on(fromActions.updateOrganizationTier.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.updateOrganizationTier.success, (state: FeatureState, action) => {
        let updatedState = { ...state, error: undefined };
        if (state.searchResults !== undefined) {
            const updatedSearchResults = state.searchResults.map((org) => {
                if (org.organizationId === action.payload.id) {
                    return { ...org, tier: action.payload.tier };
                }
                return org;
            });

            updatedState = { ...updatedState, searchResults: updatedSearchResults };
        }
        if (updatedState.organizations === undefined) {
            return updatedState;
        }
        const organizationIndex = updatedState.organizations.findIndex(
            (organization) => organization.id === action.payload.id,
        );
        const updatedOrganization = { ...updatedState.organizations[organizationIndex], tier: action.payload.tier };
        const updatedOrganizationsList = [...updatedState.organizations];
        updatedOrganizationsList[organizationIndex] = updatedOrganization;
        return { ...updatedState, organizations: updatedOrganizationsList };
    }),
    on(fromActions.updateOrganizationTier.successforownorg, (state: FeatureState, action) => {
        const updatedOrganization = { ...state.currentOrganization, tier: action.payload.tier };
        return { ...state, error: undefined, currentOrganization: updatedOrganization };
    }),
    on(fromActions.updateOrganizationAbcp.success, (state: FeatureState, action) => ({
        ...state,
        searchResults: state.searchResults?.map((org) =>
            org.organizationId === action.payload.id ? { ...org, abcpPreview: action.payload.enabled } : org,
        ),
    })),
    on(fromActions.updateOrganizationLocale.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.updateOrganizationLocale.success, (state: FeatureState, action) => {
        let updatedState = { ...state, error: undefined };
        if (state.searchResults !== undefined) {
            const updatedSearchResults = state.searchResults.map((org) => {
                if (org.organizationId === action.payload.id) {
                    return { ...org, locale: action.payload.locale };
                }
                return org;
            });

            updatedState = { ...updatedState, searchResults: updatedSearchResults };
        }
        return updatedState;
    }),
    on(fromActions.fcpManagement.updateFcpSuccess, (state: FeatureState, action) => {
        const organizationIndex = state.organizations.findIndex(
            (organization) => organization.id === action.organizationId,
        );

        const updatedOrganization = {
            ...state.organizations[organizationIndex],
            fcpActivationDetails: action.fcpActivation,
        };
        const updatedOrganizationsList = [...state.organizations];

        updatedOrganizationsList[organizationIndex] = updatedOrganization;

        return { ...state, error: undefined, organizations: updatedOrganizationsList };
    }),

    on(fromActions.fcpManagement.askForFcpApprovalSuccess, (state: FeatureState, action) => {
        const activeOrganization = { ...state.currentOrganization };
        activeOrganization.fcpActivationDetails = action.fcpActivation;
        return { ...state, error: undefined, currentOrganization: activeOrganization };
    }),

    on(fromActions.fcpManagement.deactivateFcpSuccess, (state: FeatureState, action) => {
        const activeOrganization = { ...state.currentOrganization };
        activeOrganization.fcpActivationDetails = action.fcpActivation;
        return { ...state, error: undefined, currentOrganization: activeOrganization };
    }),

    on(fromActions.cpmmActivation.changeCpmmStateSuccess, (state: FeatureState, action) => {
        if (state.organizations === undefined) {
            // this is the case if we are not in admin gui and changing the state of current organization
            const activeOrganization = { ...state.currentOrganization };
            activeOrganization.cpmmActivationDetails = action.cpmmActivation;

            return { ...state, error: undefined, currentOrganization: activeOrganization };
        }
        const organizationIndex = state.organizations.findIndex(
            (organization) => organization.id === action.organizationId,
        );

        const updatedOrganization = {
            ...state.organizations[organizationIndex],
            cpmmActivationDetails: action.cpmmActivation,
        };
        const updatedOrganizationsList = [...state.organizations];

        updatedOrganizationsList[organizationIndex] = updatedOrganization;

        return { ...state, error: undefined, organizations: updatedOrganizationsList };
    }),

    on(fromActions.updateOrganizationState.failure, (state: FeatureState, action) => ({
        ...state,
        organizations: undefined,
        error: action.payload,
    })),

    on(fromActions.updateOrganizationSettings.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.updateOrganizationSettings.success, (state: FeatureState, action) => ({
        ...state,
        currentOrganizationSettings: action.payload,
        error: undefined,
    })),
    on(fromActions.updateOrganizationSettings.failure, (state: FeatureState, action) => ({
        ...state,
        currentOrganizationSettings: undefined,
        error: action.payload,
    })),

    on(fromActions.getOrganizationSettings.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.getOrganizationSettings.success, (state: FeatureState, action) => ({
        ...state,
        currentOrganizationSettings: action.payload,
        error: undefined,
    })),
    on(fromActions.getOrganizationSettings.failure, (state: FeatureState, action) => ({
        ...state,
        currentOrganizationSettings: undefined,
        error: action.payload,
    })),

    // Users of Organization

    on(fromActions.inviteUsers.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.inviteUsers.success, (state: FeatureState) => ({
        ...state,
        error: undefined,
    })),
    on(fromActions.inviteUsers.failure, (state: FeatureState, action) => ({
        ...state,
        error: action.payload,
    })),
    on(fromActions.inviteReferrals.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.inviteReferrals.success, (state: FeatureState) => ({
        ...state,
        error: undefined,
    })),
    on(fromActions.inviteReferrals.failure, (state: FeatureState, action) => {
        return {
            ...state,
            error: action.payload,
        };
    }),
    on(fromActions.inviteReferrals.accept, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.inviteReferrals.acceptReferralInvitationSuccess, (state: FeatureState) => ({
        ...state,
        error: undefined,
    })),
    on(fromActions.inviteReferrals.acceptReferralInvitationFailure, (state: FeatureState, action) => {
        return {
            ...state,
            error: action.payload,
        };
    }),
    on(fromActions.removeUsers.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.removeUsers.success, (state: FeatureState, action) => {
        return adapter.removeMany(action.payload, { ...state });
    }),
    on(fromActions.removeUsers.failure, (state: FeatureState, action) => ({
        ...state,
        error: action.payload,
    })),
    on(fromActions.updateUserRole.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.updateUserRole.success, (state: FeatureState, action) => {
        const user = state.entities[action.payload.id];
        if (user) {
            const updatedUser = {
                ...user,
                organizationRole: action.payload.organizationRole,
                roles: action.payload.roles,
            };
            return adapter.updateOne({ id: action.payload.id, changes: updatedUser }, state);
        }
        return state;
    }),
    on(fromActions.updateUserRole.failure, (state: FeatureState, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromActions.bulkUpdateUserRole.do, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromActions.bulkUpdateUserRole.success, (state: FeatureState, action) => {
        const updates = action.payload.map(user => ({
            id: user.id,
            changes: {
                organizationRole: user.organizationRole,
                roles: user.roles,
            }
        }));
        return adapter.updateMany(updates, state);
    }),
    on(fromActions.bulkUpdateUserRole.failure, (state: FeatureState, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromActions.getUsers.do, (state) => ({
        ...state,
        error: undefined,
    })),
    on(fromActions.getUsers.success, (state, action) => {
        return adapter.setAll(action.payload, { ...state });
    }),
    on(fromActions.getUsers.failure, (state, action) => ({
        ...state,
        error: action.payload,
    })),

    on(fromLicenceActions.licenceActions.createLicenceSuccess, (state: FeatureState, action) => {
        if (!state.organizations) {
            return state;
        }
        const organization = state.organizations.find((org) => org.id === action.payload.organizationId);
        if (organization) {
            organization.licenceData = [...organization.licenceData, action.payload];
        }

        return { ...state, organizations: [...state.organizations] };
    }),
    on(fromLicenceActions.licenceActions.createLicenceSuccess, (state: FeatureState, action) => {
        if (!state.searchResults) {
            return state;
        }
        const organization = state.searchResults.find((org) => org.organizationId === action.payload.organizationId);
        if (organization) {
            organization.licenceData = [...organization.licenceData, action.payload];
        }

        return { ...state, searchResults: [...state.searchResults] };
    }),

    on(fromLicenceActions.licenceActions.updateLicenceSuccess, (state: FeatureState, action) => {
        if (!state.organizations) {
            return state;
        }
        const organization = state.organizations.find((org) => org.id === action.payload.organizationId);
        if (organization) {
            organization.licenceData = organization.licenceData.map((licence) =>
                licence.packageId === action.payload.packageId ? action.payload : licence,
            );
        }

        return { ...state, organizations: [...state.organizations] };
    }),
    on(fromLicenceActions.licenceActions.updateLicenceSuccess, (state: FeatureState, action) => {
        if (!state.searchResults) {
            return state;
        }
        const organization = state.searchResults.find((org) => org.organizationId === action.payload.organizationId);
        if (organization) {
            organization.licenceData = organization.licenceData.map((licence) =>
                licence.packageId === action.payload.packageId ? action.payload : licence,
            );
        }

        return { ...state, searchResults: [...state.searchResults] };
    }),

    on(fromLicenceActions.licenceActions.deleteLicenceSuccess, (state: FeatureState, action) => {
        if (!state.organizations) {
            return state;
        }
        const organization = state.organizations.find((org) => org.id === action.organizationId);
        if (organization) {
            organization.licenceData = organization.licenceData.filter(
                (licence) => licence.packageId !== action.packageId,
            );
        }

        return { ...state, organizations: [...state.organizations] };
    }),
    on(fromLicenceActions.licenceActions.deleteLicenceSuccess, (state: FeatureState, action) => {
        if (!state.searchResults) {
            return state;
        }
        const organization = state.searchResults.find((org) => org.organizationId === action.organizationId);
        if (organization) {
            organization.licenceData = organization.licenceData.filter(
                (licence) => licence.packageId !== action.packageId,
            );
        }

        return { ...state, searchResults: [...state.searchResults] };
    }),

    on(fromUserManagementActions.userManagement.deleteUserSuccess, (state: FeatureState, action) => {
        if (!state.searchResults) {
            return state;
        }
        // Find the organization that contains the user to be deleted
        const organization = state.searchResults.find((org) => 
            org.users.some((user) => user.internalId === action.payload)
        );
        if (organization) {
            // Remove the user from the organization's users array
            organization.users = organization.users.filter(
                (user) => user.internalId !== action.payload,
            );
            if (organization.users.length === 0) {
                // remove organization
                state.searchResults = state.searchResults.filter(
                    (org) => org.organizationId !== organization.organizationId,
                );
            }
        }

        return { ...state, searchResults: [...state.searchResults] };
    }),

    on(fromActions.inviteUsers.withdrawsuccess, (state: FeatureState, action) => {
        return adapter.removeMany(action.payload, state);
    }),

    on(fromActions.inviteUsers.success, (state: FeatureState, action: ActionWithPayload<InvitationsDto>) => {
        const successfullInvitees = [];
        for (const { email, uuid } of action.payload.successfulInvitations) {
            successfullInvitees.push({
                email,
                id: uuid,
                firstName: '',
                lastName: '',
                isInvitee: true,
                createdAt: new Date(),
            });
        }
        return adapter.addMany(successfullInvitees, state);
    }),

    on(fromActions.inviteUsers.resendinvitationsuccess, (state: FeatureState, action) => {
        const { oldUuid, newInvitation } = action;
        const user = state.entities[oldUuid];
        if (user) {
            const invitation = {
                email: newInvitation.email,
                id: newInvitation.uuid,
                firstName: '',
                lastName: '',
                isInvitee: true,
            } as GetUserDto;
            return adapter.removeOne(oldUuid, adapter.addOne(invitation, state));
        }

        return state;
    }),
    on(fromLicenceActions.licenceActions.getLicenceStatus, (state: FeatureState) => ({ ...state, error: undefined })),
    on(fromLicenceActions.licenceActions.getLicenceStatusSuccess, (state: FeatureState, action) => ({
        ...state,
        licenceStatus: action.payload,
    })),
    on(fromLicenceActions.licenceActions.getLicenceStatusFailure, (state: FeatureState, action) => ({
        ...state,
        licenceStatus: undefined,
        error: action.payload,
    })),
);

export function reducer(state: FeatureState | undefined, action: Action) {
    return organizationReducer(state, action);
}
